import { Clock, User } from "lucide-react";
import { Link } from "react-router-dom";
import type { Article } from "../types";
import { formatDate } from "../utils/date";

type Props = {
  article: Article;
  variant?: "cover" | "large" | "featured" | "compact" | "horizontal" | "numbered";
  rankIndex?: number;
};

export function ArticleCard({ article, variant = "compact", rankIndex }: Props) {
  const isCover = variant === "cover" || variant === "large";
  const isHorizontal = variant === "horizontal";
  const isNumbered = variant === "numbered";
  const catSlug = article.category?.slug || "general";

  return (
    <article
      className={`mag-card mag-card-${variant} mag-cat-card-${catSlug}`}
      data-category={catSlug}
    >
      {/* Visual Rank for numbered stories */}
      {isNumbered && typeof rankIndex === "number" && (
        <span className={`mag-card-rank mag-rank-${rankIndex}`}>
          {String(rankIndex).padStart(2, "0")}
        </span>
      )}

      {/* Article Image Container */}
      <Link
        className="mag-card-media"
        to={`/article/${article.slug}`}
        aria-label={article.title}
      >
        {article.image?.url ? (
          <img
            src={article.image.url}
            srcSet={
              article.image.responsive
                ? Object.entries(article.image.responsive)
                    .map(([w, src]) => `${src} ${w}w`)
                    .join(", ")
                : undefined
            }
            sizes={
              isCover
                ? "(max-width: 860px) 100vw, 800px"
                : isHorizontal
                ? "160px"
                : "(max-width: 860px) 100vw, 420px"
            }
            alt={article.image.alt_text || article.title}
            loading={isCover ? "eager" : "lazy"}
            className="mag-media-img"
          />
        ) : (
          <div className="mag-media-fallback">
            <span>ZimSmart Journal</span>
          </div>
        )}

        {/* Category kicker pill floating on cover images */}
        {isCover && (
          <span className={`mag-cover-badge mag-cat-badge-${catSlug}`}>
            {article.category.name}
          </span>
        )}
      </Link>

      {/* Card Content / Typography */}
      <div className="mag-card-body">
        <div className="mag-card-header">
          {!isCover && (
            <Link className={`mag-kicker mag-cat-kicker-${catSlug}`} to={`/category/${article.category.slug}`}>
              <span className="mag-kicker-dot" />
              <span>{article.category.name}</span>
            </Link>
          )}

          {article.disclosure_label && article.disclosure_label !== "editorial" ? (
            <span className="mag-disclosure-badge">
              {article.disclosure_label.replace("_", " ")}
            </span>
          ) : null}
        </div>

        <h3 className="mag-card-title">
          <Link to={`/article/${article.slug}`}>{article.title}</Link>
        </h3>

        {article.subtitle && (isCover || variant === "featured") ? (
          <p className="mag-card-subtitle">{article.subtitle}</p>
        ) : null}

        {article.excerpt && (isCover || isHorizontal || variant === "featured") ? (
          <p className="mag-card-excerpt">{article.excerpt}</p>
        ) : null}

        <div className="mag-card-meta">
          <div className="mag-author-byline">
            <User size={13} className="mag-meta-icon" />
            <Link to={`/author/${article.author.slug}`} className="mag-author-name">
              {article.author.name}
            </Link>
          </div>
          <span className="mag-meta-dot">•</span>
          <span className="mag-publish-date">{formatDate(article.published_at)}</span>
          <span className="mag-meta-dot">•</span>
          <span className="mag-reading-time">
            <Clock size={12} className="inline mr-1" />
            {article.reading_time} min read
          </span>
        </div>
      </div>
    </article>
  );
}
